import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useDispatch } from "react-redux"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { checkSubmissionsAvailable, getWaiverSubmissionsByTripBooking } from "store/digital-waiver"
import { getBooking } from "store/bookings"
import ParticipatingModal from "pagesV2/pro/Embedded/BookingConfirmation/modal/Participating"
import { SignDocumentModal } from "pagesV2/pro/Embedded/BookingConfirmation/modal/SignDocument"
import CustomQuestions from "pagesV2/pro/Embedded/BookingConfirmation/modal/CustomQuestions"
import styles from "./WidgetCheckout.module.css"

// Sign Waiver / Answer Questions on the success modal. Opens the same waiver and questionnaire
// modals as the old confirmation page (TripsPreview), so the guest finishes them inside the widget:
// a waiver already started opens straight in the signing modal, otherwise the guest first picks who
// is signing. Each button only shows when the booking has a waiver / questionnaire. The booking is
// reloaded first (as the old confirmation page does): its waivers are only attached once it's paid.
// Each booking keeps its own waiver progress; the waiver modals read the store's copy, so Sign Waiver
// loads this booking's into the store before opening them (several bookings share one success screen).
const BookingSuccessActions = ({ booking: paidBooking, justify }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [submissions, setSubmissions] = useState(null)
  const [isOpeningWaiver, setIsOpeningWaiver] = useState(false)
  const [openModal, setOpenModal] = useState(null)
  const [booking, setBooking] = useState(paidBooking)
  const [refreshKey, setRefreshKey] = useState(0)
  const hasWaivers = booking?.has_waivers !== false && booking?.booking_waivers?.length > 0
  const questionnaireId = booking?.booking_questionnaire_id
  const guestData = submissions?.guest_data
  const pendingSubmission = guestData?.[0]?.pending_submission

  const loadSubmissions = (uuid) =>
    dispatch(getWaiverSubmissionsByTripBooking(uuid, localStorage.getItem("guest_token") || undefined)).then((result) => {
      setSubmissions(result)
      return result
    })

  useEffect(() => {
    if (!paidBooking?.uuid) return
    dispatch(getBooking(paidBooking.uuid, true))
      .then((fresh) => fresh?.uuid && setBooking({ ...paidBooking, ...fresh }))
      .catch(() => {})
  }, [paidBooking, dispatch])

  useEffect(() => {
    if (!hasWaivers || !booking?.uuid) return
    loadSubmissions(booking.uuid).catch(() => setSubmissions((prev) => prev || {}))
  }, [hasWaivers, booking?.uuid, refreshKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignWaiver = async () => {
    setIsOpeningWaiver(true)
    const fresh = await loadSubmissions(booking.uuid).catch(() => submissions)
    setIsOpeningWaiver(false)
    dispatch(checkSubmissionsAvailable(booking.uuid)).catch(() => {})
    setOpenModal(fresh?.guest_data?.[0]?.pending_submission?.length > 0 ? "sign" : "participants")
  }

  const handleCloseWaiver = () => {
    setOpenModal(null)
    setRefreshKey((key) => key + 1)
  }

  if (!hasWaivers && !questionnaireId) return null

  return (
    <>
      <Flex gap={8} wrap="wrap" justify={justify} className={styles.successActions}>
        {hasWaivers && (
          <Button type="primary" shape="round" size="large" loading={isOpeningWaiver || (!submissions && !openModal)} onClick={handleSignWaiver}>
            {t("booking_widget.success.sign_waiver")}
          </Button>
        )}
        {questionnaireId && (
          <Button type="primary" shape="round" size="large" onClick={() => setOpenModal("questions")}>
            {t("booking_widget.success.answer_questions")}
          </Button>
        )}
      </Flex>

      {openModal === "participants" && (
        <ParticipatingModal
          visible
          uuid={booking.uuid}
          userId={booking.client?.id}
          bookingWaivers={booking.booking_waivers}
          tripBookingId={booking.id}
          sharedWaiverId={null}
          childCount={booking.child_count}
          onClose={handleCloseWaiver}
          dobDefaultPickerValue
        />
      )}
      {openModal === "sign" && (
        <SignDocumentModal
          open
          uuid={booking.uuid}
          id={booking.client?.id}
          pendingSubmission={pendingSubmission}
          waiverSubmissionId={pendingSubmission?.[0]?.waiver_submission_id}
          bookingWaivers={booking.booking_waivers}
          waiver={booking.booking_waivers.find((waiver) => waiver.waiver_id === pendingSubmission?.[0]?.waiver_id)}
          selected={pendingSubmission?.[0]?.waiver_id}
          signedDocumentsCount={guestData?.[0]?.signed_documents ?? 0}
          totalDocumentCount={guestData?.[0]?.total_documents ?? pendingSubmission?.length}
          onCancel={handleCloseWaiver}
        />
      )}
      {openModal === "questions" && (
        <CustomQuestions
          visible
          uuid={booking.uuid}
          bookingQuestionnaireId={questionnaireId}
          questionnaireEditable={booking.questionnaire_editable}
          onClose={() => setOpenModal(null)}
        />
      )}
    </>
  )
}

BookingSuccessActions.propTypes = {
  booking: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    uuid: PropTypes.string,
    has_waivers: PropTypes.bool,
    booking_waivers: PropTypes.array,
    booking_questionnaire_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    questionnaire_editable: PropTypes.bool,
    child_count: PropTypes.number,
    client: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) })
  }).isRequired,
  justify: PropTypes.oneOf(["center", "flex-start", "flex-end"])
}

BookingSuccessActions.defaultProps = {
  justify: "center"
}

export default BookingSuccessActions
